import type { CounterpartySnapshot } from '../counterparties/counterparties.dto.js';
import type {
  ShipmentReportDailyRow,
  ShipmentReportTotalRow,
} from './shipments-shared.dto.js';

export const shipmentFormalReportDocumentTypeValues = [
  'REGULAR_SHIPPING_REPORT',
  'COVER_SHIPPING_REPORT',
] as const;
export type ShipmentFormalReportDocumentType =
  (typeof shipmentFormalReportDocumentTypeValues)[number];

export const shipmentFormalReportStatusValues = ['READY', 'DRAFT_INCOMPLETE'] as const;
export type ShipmentFormalReportStatus = (typeof shipmentFormalReportStatusValues)[number];

export const shipmentFormalReportDailySummarySourceValues = [
  'SHIPMENT_PURCHASES',
  'COVER_OWN_PURCHASES_ONLY',
] as const;
export type ShipmentFormalReportDailySummarySource =
  (typeof shipmentFormalReportDailySummarySourceValues)[number];

export const shipmentFormalReportDoriisEvidenceStatusValues = [
  'LINKED_EVIDENCE_PRESENT',
  'EVIDENCE_NOT_LINKED',
] as const;
export type ShipmentFormalReportDoriisEvidenceStatus =
  (typeof shipmentFormalReportDoriisEvidenceStatusValues)[number];

export interface ShipmentFormalReportMaterial {
  materialId: string;
  displayName: string | null;
}

export interface ShipmentFormalReportPeriod {
  rangeStartAt: string;
  rangeEndAt: string;
}

export interface ShipmentFormalReportProvenance {
  sourceLabel: string;
  packetLabel: string;
  notDoriisUploadFile: true;
  notDoriisSubmissionProofUnlessLinkedEvidence: true;
  notes: string[];
}

export interface ShipmentFormalReportDailySummarySection {
  source: ShipmentFormalReportDailySummarySource;
  rows: ShipmentReportDailyRow[];
  totalRow: ShipmentReportTotalRow;
}

export interface ShipmentFormalReportShippingValuesSection {
  totalRow: ShipmentReportTotalRow;
  attachedReportCount: number;
  totalDocumentWeightLb: number;
  totalRefundValueCents: number;
}

export interface ShipmentFormalReportAttachmentRegisterRow {
  sourceShippingReportNumber: string | null;
  sourceShipper: CounterpartySnapshot;
  receivedAt: string;
  officialContributionWeightLb: number;
  officialContributionRefundValueCents: number;
  physicalReceivedWeightLb: number;
}

export interface ShipmentFormalReportAttachmentRegisterSection {
  rows: ShipmentFormalReportAttachmentRegisterRow[];
  totalAttachedReportCount: number;
  totalOfficialContributionWeightLb: number;
  totalOfficialContributionRefundValueCents: number;
}

export interface ShipmentFormalReportEvidenceSummary {
  doriisShippingReportNumber: string | null;
  receivedDate: string | null;
  weightTicketNumber: string | null;
  officialReceivedWeightLb: number | null;
  processorReceivedWeightLb: number | null;
  inboundPhysicalReceivedWeightLb: number | null;
  selectedHuPhysicalWeightLb: number | null;
  evidenceNotes: string[];
}

export interface ShipmentFormalReportDoriisGuidance {
  evidenceStatus: ShipmentFormalReportDoriisEvidenceStatus;
  message: string;
  notDoriisUploadFile: true;
}

export interface ShipmentFormalReportSignatureBlock {
  preparerName: string | null;
  preparerTitle: string | null;
  signatureDate: string | null;
}

export interface ShipmentFormalReportDocument {
  documentType: ShipmentFormalReportDocumentType;
  documentTitle: string;
  generatedAt: string;
  generatedBy: string | null;
  status: ShipmentFormalReportStatus;
  completenessWarnings: string[];
  provenance: ShipmentFormalReportProvenance;
  shipper: CounterpartySnapshot;
  receiver: CounterpartySnapshot;
  reportPeriod: ShipmentFormalReportPeriod;
  material: ShipmentFormalReportMaterial;
  dailySummary: ShipmentFormalReportDailySummarySection;
  shippingReportValues: ShipmentFormalReportShippingValuesSection;
  coverAttachmentRegister: ShipmentFormalReportAttachmentRegisterSection | null;
  evidenceSummary: ShipmentFormalReportEvidenceSummary;
  doriisGuidance: ShipmentFormalReportDoriisGuidance;
  signatureBlock: ShipmentFormalReportSignatureBlock;
}
